import type { PanInfo } from "motion/react";

interface UseSwipeNavigationOptions {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  enabled: boolean;
  threshold?: number;
}

interface DragHandlers {
  drag: "x";
  dragConstraints: { left: 0; right: 0 };
  dragElastic: number;
  onDragEnd: (event: PointerEvent, info: PanInfo) => void;
}

export function useSwipeNavigation(options: UseSwipeNavigationOptions): {
  dragHandlers: DragHandlers;
} {
  const { onSwipeLeft, onSwipeRight, enabled, threshold = 50 } = options;

  const handleDragEnd = (_event: PointerEvent, info: PanInfo) => {
    if (!enabled) return;

    const { x: offsetX, y: offsetY } = info.offset;

    // Direction lock: ignore if vertical gesture dominates
    if (Math.abs(offsetY) > Math.abs(offsetX)) {
      return;
    }

    // Swipe left (negative X) → next requirement
    if (offsetX < -threshold) {
      onSwipeLeft();
    }

    // Swipe right (positive X) → previous requirement
    if (offsetX > threshold) {
      onSwipeRight();
    }
  };

  // Return empty handlers when disabled
  if (!enabled) {
    return {
      dragHandlers: {
        drag: "x",
        dragConstraints: { left: 0, right: 0 },
        dragElastic: 0.1,
        onDragEnd: () => {},
      },
    };
  }

  return {
    dragHandlers: {
      drag: "x",
      dragConstraints: { left: 0, right: 0 },
      dragElastic: 0.1,
      onDragEnd: handleDragEnd,
    },
  };
}
